/* eslint-disable no-undef */
import React, { useRef, useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  IconButton,
  Box,
  LinearProgress,
  Slider
} from '@mui/material';
import {
  PlayArrow,
  Pause,
  VolumeUp,
  Headphones
} from '@mui/icons-material';
import { useMutation } from '@tanstack/react-query';
import { MarketPodcast } from '@horwath-htl/shared-types';
import { ApiClient } from '../../services/api';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { formatDuration } from '../../utils/formatters';
import { API_URL } from '../../utils/config';

interface PodcastPlayerProps {
  podcast: MarketPodcast;
}

export const PodcastPlayer: React.FC<PodcastPlayerProps> = ({ podcast }) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const { authenticatedFetch } = useAuthenticatedFetch();
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(podcast.durationSeconds || 0);
  const [volume, setVolume] = useState(1);
  const [streamUrl, setStreamUrl] = useState<string | null>(null);

  const apiClient = new ApiClient({ authenticatedFetch });

  const streamMutation = useMutation({
    mutationFn: async () => {
      const response = await apiClient.get(
        `${API_URL}/api/marketcompass/markets/${podcast.marketId}/podcasts/${podcast.id}/stream-url`
      );
      return response.data;
    },
    onSuccess: (data) => {
      setStreamUrl(data.streamUrl);
      if (audioRef.current) {
        audioRef.current.src = data.streamUrl;
        audioRef.current.play();
        setIsPlaying(true);
      }
    }
  });

  const handlePlayPause = () => {
    if (!streamUrl && !streamMutation.isPending) {
      streamMutation.mutate();
    } else if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleSeek = (event: Event, value: number | number[]) => {
    const time = value as number;
    setCurrentTime(time);
    if (audioRef.current) {
      audioRef.current.currentTime = time;
    }
  };

  const handleVolumeChange = (event: Event, value: number | number[]) => {
    const vol = value as number;
    setVolume(vol);
    if (audioRef.current) {
      audioRef.current.volume = vol;
    }
  };

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Headphones sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6" component="h3" sx={{ flex: 1 }}>
            {podcast.title}
          </Typography>
        </Box>

        {podcast.description && (
          <Typography variant="body2" color="text.secondary" paragraph>
            {podcast.description}
          </Typography>
        )}

        <audio
          ref={audioRef}
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onEnded={() => setIsPlaying(false)}
        />

        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <IconButton
            onClick={handlePlayPause}
            disabled={streamMutation.isPending}
            color="primary"
          >
            {isPlaying ? <Pause /> : <PlayArrow />}
          </IconButton>

          <Box sx={{ flex: 1, mx: 2 }}>
            <Slider
              value={currentTime}
              max={duration}
              onChange={handleSeek}
              disabled={!streamUrl}
              size="small"
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="caption">
                {formatDuration(currentTime)}
              </Typography>
              <Typography variant="caption">
                {formatDuration(duration)}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', width: 120 }}>
            <VolumeUp sx={{ mr: 1 }} />
            <Slider
              value={volume}
              max={1}
              step={0.1}
              onChange={handleVolumeChange}
              size="small"
            />
          </Box>
        </Box>

        {streamMutation.isPending && (
          <LinearProgress sx={{ mt: 2 }} />
        )}
      </CardContent>
    </Card>
  );
};